// Reine Logik der Arbeitsliste (Schritt 4, ADR 0243): welche Knoepfe ein Schritt hat, was auf der Karte
// steht, wie die Sperr-Meldung lautet. Ohne React, damit vitest sie prueft (vitest laeuft hier nur in
// einer Node-Umgebung, ohne Komponenten-Render).

/** Die Schritt-Arten, die der Ticket-Automat vergibt (`config.LINKEDIN_KANAELE`). */
export type SchrittKanal =
  | 'li_anfrage' | 'li_annahme_pruefen' | 'li_anschreiben' | 'li_nachfassen' | 'li_antworten'
  | 'li_aufwaermen';

export const SCHRITT_TITEL: Record<SchrittKanal, string> = {
  li_anfrage: 'Freundschaftsanfrage schicken',
  li_annahme_pruefen: 'Prüfen: angenommen?',
  li_anschreiben: 'Erste Nachricht schreiben',
  li_nachfassen: 'Nachfassen',
  li_antworten: 'Antwort abschicken',
  li_aufwaermen: 'Aufwärmen',
};

/** Der Knopf-Satz je Schritt. `composer` = Pitch-/Follow-up-Fenster, `ergebnis` = Annahme-Auswahl,
 *  `antwort` = Text des Besitzers kopieren und „gesendet“. „Zurücklegen“ gibt es immer. */
export type KarteAktion = 'anfrage_composer' | 'ergebnis_waehlen' | 'pitch_composer' | 'followup_composer'
  | 'antwort_senden' | 'aufwaermen';

export function karteAktion(kanal: string | null | undefined): KarteAktion | null {
  switch (kanal) {
    case 'li_anfrage': return 'anfrage_composer';
    case 'li_annahme_pruefen': return 'ergebnis_waehlen';
    case 'li_anschreiben': return 'pitch_composer';
    case 'li_nachfassen': return 'followup_composer';
    case 'li_antworten': return 'antwort_senden';
    case 'li_aufwaermen': return 'aufwaermen';
    default: return null;
  }
}

/** Die drei Ergebnisse, die „Annahme prüfen“ anbietet (Vokabular `services/sheets_ergebnisse`). */
export const ANNAHME_ERGEBNISSE: { wert: 'angenommen' | 'geghostet' | 'blockiert'; label: string }[] = [
  { wert: 'angenommen', label: 'Angenommen' },
  { wert: 'geghostet', label: 'Geghostet' },
  { wert: 'blockiert', label: 'Blockiert' },
];

export interface KontoRest {
  id: number; name: string | null; rest: number; tag_rest: number; woche_rest: number; voll: boolean;
}

/** „Konto 2: noch 14 · Konto 5: voll“ — eine Zeile, in der Reihenfolge des Servers. */
export function kontoZeile(konten: KontoRest[]): string {
  if (!konten.length) return '';
  return konten.map((k) => `${k.name ?? `Konto ${k.id}`}: ${k.voll ? 'voll' : `noch ${k.rest}`}`).join(' · ');
}

/** Der Hinweis, wenn keine Anfrage mehr rausgehen kann. */
export function alleVollText(alleVoll: boolean): string | null {
  return alleVoll ? 'Alle Konten voll bis Montag. Es kommen keine Anfragen mehr.' : null;
}

/** Ist das gewählte Konto voll? Ohne Wahl (oder unbekanntes Konto) sperrt nichts. */
export function gewaehltesKontoVoll(konten: KontoRest[], kontoId: number | null | undefined): boolean {
  if (kontoId == null) return false;
  return konten.find((k) => k.id === kontoId)?.voll ?? false;
}

/** Die Meldung zu einer 409-Antwort der Besitz-Sperre (`firmen_besitz.pruefen_und_beanspruchen`).
 *  `null`, wenn der Fehler keine Besitz-Sperre ist. */
export function besitzSperreText(detail: Record<string, unknown> | undefined | null): string | null {
  if (!detail) return null;
  const grund = detail.grund;
  if (grund === 'gehoert_fraud0') return 'Diese Firma ist für fraud0 reserviert.';
  if (grund === 'fremder_besitzer') {
    const b = detail.besitzer as { name?: string } | null | undefined;
    return `Diese Firma gehört ${b?.name?.trim() || 'einem anderen Setter'}.`;
  }
  return null;
}

/** Die Meldung zu `409 {grund: "in_arbeit"}` (`_deny_in_arbeit`): die Reservierung (Standard 60 Minuten)
 *  ist abgelaufen und die Aufgabe liegt jetzt bei jemand anderem. `null`, wenn der Fehler etwas anderes ist. */
export function inArbeitText(detail: Record<string, unknown> | undefined | null): string | null {
  if (!detail || detail.grund !== 'in_arbeit') return null;
  const name = typeof detail.name === 'string' && detail.name.trim() ? detail.name.trim() : 'jemand anderem';
  return `Die Zeit für diese Aufgabe ist abgelaufen, sie liegt jetzt bei ${name}. `
    + `Schon gesendet? Dann sag ${name === 'jemand anderem' ? 'dem Kollegen' : name} Bescheid.`;
}

/** „Von Bianca übernommen“ — nur gesetzt, wenn der Server das Schild mitgibt (eine Woche). */
export function uebernommenText(von: string | null | undefined): string | null {
  return von ? `von ${von} übernommen` : null;
}

export interface ListenZeile {
  lead_id: number; domain: string; kanal: string | null; person_key: string | null;
  faellig_am: string | null; zustand: string | null; ueberfaellig: boolean;
}

const KANAL_TEXT: Record<string, string> = {
  anruf: 'Anrufen', mail_schicken: 'Mail schicken', mail_nachfassen: 'Mail nachfassen',
  text_schreiben: 'Antwort-Text schreiben',
};

/** Die Beschriftung einer Zeile in „Meine Firmen“. Unbekannte Kanäle zeigen ihren Namen, nie nichts. */
export function zeilenAktion(kanal: string | null | undefined): string {
  if (!kanal) return 'Offen';
  return KANAL_TEXT[kanal] ?? SCHRITT_TITEL[kanal as SchrittKanal] ?? kanal;
}

/** „seit 3 Tg. fällig“ / „fällig in 2 Std.“, relativ zu `jetzt` (ms). */
export function faelligText(iso: string | null | undefined, jetzt: number): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const min = Math.round((t - jetzt) / 60000);
  const a = Math.abs(min);
  const spanne = a < 60 ? `${a} Min.` : a < 1440 ? `${Math.round(a / 60)} Std.` : `${Math.round(a / 1440)} Tg.`;
  return min <= 0 ? `seit ${spanne} fällig` : `fällig in ${spanne}`;
}

/** Die kleine Zahl im Menue: leer bei 0, „9+“ ab zehn. */
export function badgeText(n: number): string {
  if (!n || n < 1) return '';
  return n > 9 ? '9+' : String(n);
}

/** Eine Person auf der Firmenkarte (ADR 0269): `dran` = die Person, die als Naechstes angerufen wird. */
export interface KartenPerson { person_key: string | null; dran?: boolean }

/**
 * Wer auf der Karte gewaehlt werden darf (ADR 0269): nur die markierte Person. Steht keine Liste an der Zeile
 * (aelterer Server, Mail-Schritt, Vorrat-Person im Schritt), bleibt es bei der Zeile des Schritts. Die Karte
 * zeigt alle, der Waehlknopf haengt nur an dem, was diese Funktion zurueckgibt.
 */
export function waehlbarePerson<T extends KartenPerson>(personen: T[] | null | undefined,
                                                        zeile: T | null | undefined): T | null {
  const dran = (personen ?? []).find((p) => p.dran);
  return dran ?? zeile ?? null;
}

/** Darf diese Person gewaehlt werden? (Wahr nur fuer `waehlbarePerson`.) */
export function darfWaehlen<T extends KartenPerson>(person: T, personen: T[] | null | undefined,
                                                    zeile: T | null | undefined): boolean {
  const w = waehlbarePerson(personen, zeile);
  return w != null && w.person_key === person.person_key;
}

/** „3 anrufbar · 10 im Vorrat“, leer ohne Zahlen. */
export function bestandText(b: { anrufbar: number; vorrat: number } | null | undefined): string {
  if (!b) return '';
  const teile = [`${b.anrufbar} anrufbar`];
  if (b.vorrat > 0) teile.push(`${b.vorrat} im Vorrat`);
  return teile.join(' · ');
}

/** Aufwärmen (ADR 0320): Link zur LinkedIn-Seite der Firma, sonst eine LinkedIn-Suche nach der Domain. */
export function aufwaermLink(firmaUrl: string | null | undefined, domain: string | null | undefined): string | null {
  const u = (firmaUrl ?? '').trim();
  if (u) return u;
  const d = (domain ?? '').trim();
  return d ? `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(d)}` : null;
}
